"use client"

import { ColorType } from "@/app/types/color";
import { ProductDetailType, ProductVariantAxisType, ProductVariantAxisValueType } from "@/app/types/product-site";
import { Box, Button, colors, Typography } from "@mui/material";
import TextOverflow from "@v2/components/styled/TextOverflow";
import { uniqueByKey } from "@v2/helpers/optimizedHelpers";
import { useCallback } from "react";
import { ColorPartGroup, colorPartsOf } from "@v2/lib/colorParts";
import { usePdpVariant } from "./PdpVariantContext";
import { isPurchasable, pickVariantForAxis, variantAxisValueId, variantPickerAxes } from "../utils";

export default function PdpPickableOptions() {
    const { showProduct, details, variantAxes, selectVariantById } = usePdpVariant();
    const axes = variantPickerAxes(showProduct, details, variantAxes);

    if (!axes) return <LegacyPdpPickableOptions />;

    const candidatesFor = (axis: ProductVariantAxisType, value: ProductVariantAxisValueType) =>
        details.filter(product => product.is_available !== false && variantAxisValueId(product, axis) === value.id);

    const pickValue = (axis: ProductVariantAxisType, value: ProductVariantAxisValueType) => () => {
        const best = pickVariantForAxis(details, axes, showProduct, axis, value.id);
        if (best && best.product_id !== showProduct.product_id) selectVariantById(best.product_id);
    };

    // A value looks in stock only when the variant a click lands on can be
    // bought, so the faded values show which choices would end on "out of stock".
    const landsInStock = (axis: ProductVariantAxisType, value: ProductVariantAxisValueType) => {
        const target = pickVariantForAxis(details, axes, showProduct, axis, value.id);
        return target ? isPurchasable(target) : false;
    };

    return (
        <Box sx={{ mt: 1 }}>
            {axes.map(axis => {
                const activeId = variantAxisValueId(showProduct, axis);
                const choices = axis.values.map(value => {
                    const candidates = candidatesFor(axis, value);
                    return {
                        value,
                        available: candidates.length > 0,
                        inStock: candidates.some(product => product.quantity > 0),
                        landsInStock: candidates.length > 0 && landsInStock(axis, value),
                    };
                });
                // Sizes and indexes keep the API order, so a crossed out value stays
                // in its place. Only colours move the out of stock ones to the end.
                if (axis.slug === 'color') choices.sort((a, b) =>
                    Number(b.available && b.inStock) - Number(a.available && a.inStock)
                    || Number(b.available) - Number(a.available)
                );

                // An axis with one value keeps its picker with that one choice selected
                // (the colour with its swatch and part line), rather than turning into text.
                return <Box key={axis.slug} sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: 15, color: colors.grey[700], fontWeight: 400 }}>
                        انتخاب {axis.title}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2, gap: 1 }}>
                        {choices.map(({ value, available, landsInStock }) => {
                            const active = activeId === value.id;
                            if (axis.slug === 'color') {
                                const parts = colorPartsOf(value);
                                const hasSwatches = parts.length > 0;
                                // Same swatch as the legacy picker: each part's colours side by side
                                // (frame, lens), and the active one shrinks into a circle inside its border.
                                return <Button key={value.id} disabled={!available}
                                    aria-label={value.label} color="inherit" onClick={pickValue(axis, value)}
                                    sx={{
                                        borderRadius: '18px', overflow: 'hidden', minWidth: 52,
                                        height: 52, width: hasSwatches ? 52 : 'auto', p: hasSwatches ? 0 : '0 12px',
                                        border: '1px solid', transition: '280ms',
                                        borderColor: active ? colors.grey[700] : 'transparent',
                                        opacity: available ? (landsInStock ? 1 : 0.6) : 0.4,
                                    }}>
                                    {!hasSwatches ? value.label : <Box sx={{
                                        display: 'flex', height: 1, width: 1, overflow: 'hidden',
                                        ...(active && { borderRadius: '50px', transition: '200ms', transform: 'scale(0.8)' }),
                                        '& > div': { height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
                                    }}>
                                        <PartColumns parts={parts} />
                                    </Box>}
                                </Button>;
                            }

                            const subtitle = value.value && value.value !== value.label
                                ? value.value + (axis.validation?.unit ? ` ${axis.validation.unit}` : '') : null;
                            return <Button key={value.id} disabled={!available} color="inherit"
                                onClick={pickValue(axis, value)} sx={{
                                    position: 'relative', borderRadius: subtitle ? '12px' : '16px', minWidth: 48, minHeight: 48,
                                    px: 1.5, bgcolor: active ? 'transparent' : colors.grey[100],
                                    border: '1px solid', borderColor: active ? colors.grey[700] : 'transparent',
                                    color: landsInStock ? colors.grey[900] : colors.grey[500],
                                    fontWeight: 500, fontFeatureSettings: '"ss02"',
                                    ...(!available && { '&::after': { content: '""', position: 'absolute',
                                        width: '70%', height: '1px', bgcolor: colors.grey[500], transform: 'rotate(-25deg)' } }),
                                }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.3 }}>
                                    <span>{value.label}</span>
                                    {subtitle && <Box component="span" sx={{ mt: 0.5, fontSize: 9, fontWeight: 400, color: colors.grey[500] }}>
                                        {subtitle}
                                    </Box>}
                                </Box>
                            </Button>;
                        })}
                    </Box>
                    {axis.slug === 'color' && colorPartsOf(showProduct).length > 0 &&
                        <TextOverflow sx={{ mt: 1, fontSize: 13, fontWeight: 300, color: colors.grey[700],
                            '& strong': { fontWeight: 400, color: colors.grey[900] } }}>
                            <PartTitles parts={colorPartsOf(showProduct)} />
                        </TextOverflow>}
                </Box>;
            })}
        </Box>
    );
}

function LegacyPdpPickableOptions() {
    const { showProduct, details: products, selectVariant, selectVariantById } = usePdpVariant();

    const hasColor = products.some(p => colorPartsOf(p).length > 0)
    const isLens = showProduct.product_class?.slug === "lenses"

    // A swatch stands for one colour code. Old lens rows hold the index in color_code
    // ("1.54", "1.60"), so there one colour came out once per index; a lens is grouped by
    // the colours it really has instead, and its variants are then picked by id.
    const groupKey = useCallback((product: ProductDetailType) => isLens
        ? colorPartsOf(product).map(part => part.slug + ':' + part.colors.map(color => color.id).join(',')).join('|')
        : product.color_code, [isLens])
    const grouped = products.reduce<Record<string, ProductDetailType[]>>((groups, product) => {
        (groups[groupKey(product)] ??= []).push(product)
        return groups
    }, {})
    const activeKey = groupKey(showProduct)
    // Colors carrying no stock at all belong at the end, whatever their priority
    // says. products already arrives stock first, so each group's head is the best
    // variant of that color and stands in for the whole group here.
    const groupedValues = Object.values(grouped).sort((a, b) =>
        (b.some(p => p.quantity > 0) ? 1 : 0) - (a.some(p => p.quantity > 0) ? 1 : 0)
        || a[0].priority - b[0].priority
    );

    const sizes = uniqueByKey(products, 'size_number').map(p => ({
        title: p.size,
        number: p.size_number
    }))
    const availableSized = hasColor
        ? uniqueByKey(grouped[activeKey], 'size_number').map(p => ({ title: p.size, number: p.size_number }))
        : sizes

    const onPickColor = useCallback((product: ProductDetailType) => () => {
        const key = groupKey(product)
        if (key === activeKey) return;
        if (isLens) {
            const group = grouped[key]
            const target = group.find(p => p.size_number === showProduct.size_number && p.quantity > 0)
                ?? group.find(p => p.quantity > 0)
                ?? group[0]
            selectVariantById(target.product_id);
            return;
        }
        const inGroupSizes = uniqueByKey(grouped[product.color_code], 'size').map(p => ({
            size_number: p.size_number,
            has_quantity: p.quantity > 0 ? 1 : 0,
        }))
        let requestSize = showProduct.size_number;
        if (
            !inGroupSizes.some(s => s.size_number === showProduct.size_number) ||
            inGroupSizes.find(s => s.size_number === showProduct.size_number)?.has_quantity === 0
        ) {
            const defaultItem = inGroupSizes.sort((a, b) => b.has_quantity - a.has_quantity)[0]
            requestSize = defaultItem.size_number
        }
        selectVariant(product.color_code, requestSize);
    }, [showProduct, grouped, groupKey, activeKey, isLens, selectVariant, selectVariantById])

    const onPickSize = useCallback((size: string | null) => () => {
        if (size === showProduct.size_number) return;
        if (isLens && hasColor) {
            const target = grouped[activeKey].find(p => p.size_number === size)
            if (target) selectVariantById(target.product_id);
            return;
        }
        selectVariant(hasColor ? showProduct.color_code : null, size);
    }, [hasColor, isLens, showProduct, grouped, activeKey, selectVariant, selectVariantById])

    const activeSizeObject = sizes.find(s => s.number === showProduct.size_number)

    return (
        <Box sx={{ mt: 1 }}>
            {hasColor && (
                <>
                    <Typography sx={{ fontSize: 15, color: colors.grey[700], fontWeight: 400 }}>انتخاب رنگ</Typography>

                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        mt: 2
                    }}>
                        {groupedValues.map((productItems) => {
                            const product = productItems[0]
                            const isActive = groupKey(product) === activeKey;

                            return (
                                <Button key={product.product_id} aria-label={product.name} color="inherit" onClick={onPickColor(product)} sx={{
                                    borderRadius: '18px',
                                    overflow: 'hidden',
                                    minWidth: 'unset',
                                    height: 52,
                                    width: 52,
                                    p: 0,
                                    outline: '1px solid',
                                    transition: '280ms',
                                    outlineColor: 'transparent',
                                    mr: 1,
                                    mb: 1,
                                    ...(isActive ? {
                                        outlineColor: colors.grey[600],
                                    } : {}),
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        borderRadius: '0',
                                        height: 1, width: 1,
                                        overflow: 'hidden',
                                        ...(isActive ? {
                                            borderRadius: '50px',
                                            transition: '200ms',
                                            transform: 'scale(0.8)'
                                        } : {}),
                                        '& > div': {
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            overflow: 'hidden',
                                        }
                                    }}>
                                        <PartColumns parts={colorPartsOf(product)} />
                                    </Box>
                                </Button>
                            )
                        })}
                    </Box>
                </>
            )}

            <TextOverflow sx={{
                fontSize: 13,
                fontWeight: 300,
                color: colors.grey[700],
                '& strong': {
                    fontWeight: 400,
                    color: colors.grey[900],
                }
            }}>
                <PartTitles parts={colorPartsOf(showProduct)} />
            </TextOverflow>

            {!!sizes.length && (
                <>
                    <Typography sx={{ fontSize: 15, color: colors.grey[700], fontWeight: 400, mt: 2 }}>انتخاب {isLens ? 'فشردگی' : 'سایز'}</Typography>
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        mt: 2
                    }}>
                        {sizes.map((sizeItem, index) => {
                            const isActive = sizeItem.number === showProduct.size_number;
                            const isAvailable = availableSized.some(i => i.number === sizeItem.number)

                            return (
                                <Button disabled={!isAvailable} key={index}
                                    color="inherit"
                                    onClick={onPickSize(sizeItem.number)} sx={{
                                        display: 'flex',
                                        overflow: 'hidden',
                                        minWidth: 'unset',
                                        borderRadius: '16px',
                                        height: 48,
                                        width: 48,
                                        outline: '1px solid',
                                        transition: '280ms',
                                        outlineColor: 'transparent',
                                        p: 0,
                                        mr: 1,
                                        mb: 1,
                                        bgcolor: colors.grey[100],
                                        '& > span._content': {
                                            fontFeatureSettings: '"ss02"',
                                            position: 'relative',
                                            top: 1,
                                            color: colors.grey[900],
                                            fontSize: 15,
                                            fontWeight: 500,
                                            ...(!isAvailable && {
                                                color: colors.grey[500]
                                            })
                                        },
                                        ...(isActive ? {
                                            outlineColor: colors.grey[600],
                                            bgcolor: 'transparent'
                                        } : {}),
                                        ...(!isAvailable && {
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                height: '70%',
                                                width: '1px',
                                                bgcolor: colors.grey[500],
                                                transform: 'rotate(45deg)'
                                            }
                                        })
                                    }}>
                                    {sizeItem ? (
                                        <span className="_content">
                                            {sizeItem.number}
                                        </span>
                                    ) : (
                                        <Box component="span" sx={{
                                            fontSize: 9, color: colors.grey[800]
                                        }}>
                                            نامشخص
                                        </Box>
                                    )}
                                </Button>
                            )
                        })}
                    </Box>
                    {activeSizeObject && activeSizeObject.number !== activeSizeObject.title && (
                        <Typography component="span" sx={{
                            fontSize: 13,
                            fontWeight: 300,
                            color: colors.grey[700],
                            '& strong': {
                                display: 'inline-block',
                                mr: 0.5,
                                fontWeight: 400,
                                color: colors.grey[900],
                            }
                        }}>
                            <strong>سایز</strong>
                            <span>
                                {activeSizeObject?.title}
                            </span>
                        </Typography>
                    )}
                </>
            )}
        </Box>
    )
}

/** One column per part, the first one full strength and the others (lens) a little lighter. */
const PartColumns = ({ parts }: { parts: ColorPartGroup<ColorType>[] }) => <>
    {parts.map((part, index) => <Box key={part.slug} sx={{ flex: 1, ...(index > 0 && { '& > span': { opacity: 0.8 } }) }}>
        {part.colors.map((color, colorIndex) => <ColorItem key={color.id + '-' + colorIndex} color={color} />)}
    </Box>)}
</>

/** «فریم مشکی لنز دودی»; a part titled «رنگ» reads «رنگ قرمز». A real space keeps the title off the colours. */
const PartTitles = ({ parts }: { parts: ColorPartGroup<ColorType>[] }) => <>
    {parts.map((part, index) => <span key={part.slug}>
        {index > 0 && ' '}<strong>{part.title}</strong>{' '}{part.colors.map(color => color.title).join(' ، ')}
    </span>)}
</>

const ColorItem = ({ color }: {
    color: ColorType
}) => {
    return <Box key={color.id} component="span" sx={{
        flex: 1,
        display: 'inline-block',
        position: 'relative',
        bgcolor: color.code ?? colors.grey[300],
        backgroundImage: color.texture ? `url('${process.env.NEXT_PUBLIC_MEDIA_URL}/api/v1/media/${color.texture.id}?height=38')` : undefined,
        backgroundSize: 'cover'
    }} />
}
